/* Everything about the documents, on one page, in the order the job is done: which system,
 * the corpus browsed as folders, the form that writes into the folder you are standing in,
 * what to write next, then what the last changes did (`Impact.tsx`).
 *
 * The upload is forwarded to the assistant's admin route, which converts, sandboxes and
 * re-indexes inside this request — genuinely slow, so the button says so.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  ConfirmModal,
  FileSelectDropzone,
  Input,
  Modal,
  MultiSelectDropdown,
  SkeletonText,
  cn,
} from '@nextleap-al/admin-ui';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Folder,
  FolderPlus,
  Pencil,
  Search,
  Trash2,
} from 'lucide-react';

import { detailOf, write } from '../api/client';
import type {
  CorpusCategory,
  CorpusDocument,
  CorpusSystems,
  CorpusText,
  CorpusTree,
  Coverage,
  DeleteResult,
  RoleVocabulary,
  GapsResponse,
  ImpactItem,
  System,
  SystemInfo,
  UploadResult,
} from '../api/types';
import { Async, PanelCard, ResultLine } from '../components/Panel';
import { Signal } from '../components/Signal';
import { AddSystemForm, EditSystemForm } from '../components/SystemAdmin';
import { SystemLogo } from '../components/SystemLogo';
import { Table } from '../components/Table';
import { TileRow } from '../components/Tiles';
import { useApi } from '../hooks/useApi';
import { num } from '../lib/format';
import { useApp } from '../state/app';
import { DocumentUse, EveryDocument } from './Coverage';
import { ImpactList, impactTiles } from './Impact';

/** The service's own ceiling, in `server/src/routes/api.ts`. Refusing at a lower number here
 *  meant the form turned away files the upload route would have accepted. */
const MAX_UPLOAD_MB = 50;

/* ------------------------------------------------------------------ picking the system */

/**
 * Whether the service answered in the shape this page reads. It used to answer
 * `systems: string[]`, and a running service one build behind still does — which rendered as
 * one nameless card per system with "No documents yet" under it, a picture of an empty corpus
 * rather than of a service that needs restarting. Cheaper to check than to debug twice.
 */
const isCurrentShape = (systems: unknown[]): systems is SystemInfo[] =>
  systems.every((entry) => typeof (entry as SystemInfo)?.key === 'string');

/**
 * One card per system, rather than a dropdown of two. A dropdown asks which of a list, and
 * that is the wrong question when the list is two products that share nothing: what the
 * corpus holds and how much of it is the thing worth seeing before choosing.
 */
function SystemPicker({
  systems,
  value,
  onChange,
  rows,
  onEdit,
}: {
  systems: SystemInfo[];
  value: string;
  onChange: (key: string) => void;
  /** The declared systems, by key — the ones there is anything to edit about, and the only
   *  source of whether one is switched on. A folder on disk that nobody declared is offered
   *  but not editable. Empty for an account that may not change systems at all. */
  rows: Map<string, System>;
  onEdit: (key: string) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Which system is this document for?"
      // Sized by the cards rather than by the card they sit in: a fixed two columns made each
      // one as wide as the page, and three lines of small print stranded in it. They fill in
      // at their own width and wrap, so a fourth system costs a slot, not a redesign.
      className="grid grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] gap-3"
    >
      {systems.map((system) => {
        const chosen = system.key === value;
        const row = rows.get(system.key);
        /* A div rather than a button, now that the card carries one. A button inside a button
         * is not valid HTML and browsers recover from it by breaking the outer one, so the
         * whole card would have stopped selecting. The click, the keyboard and the radio role
         * are all still here — they are just spelled out rather than inherited. */
        return (
          <div
            key={system.key}
            role="radio"
            aria-checked={chosen}
            aria-label={system.label}
            // Roving tabindex, which is what a radiogroup is meant to have: one stop for the
            // group, on the chosen card, and the arrow keys are the browser's own once inside.
            tabIndex={chosen ? 0 : -1}
            onClick={() => onChange(system.key)}
            onKeyDown={(event) => {
              if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault();
                onChange(system.key);
              }
            }}
            className={cn(
              'cursor-pointer rounded-[var(--radius-md)] border p-3 text-left transition-colors',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]',
              // Both cards carry the navy tint, so the pair reads as one choice; the chosen
              // one is the same tint darkened, which is a difference in weight rather than
              // in colour — and the border and the badge are what actually say "selected".
              chosen
                ? 'border-[var(--primary)] bg-[var(--primary-light)]'
                : 'border-[var(--border-light)] bg-[var(--primary-tint)] hover:border-[var(--border)]',
            )}
          >
            {/* Each product's own mark heads its card: the two are told apart by their logo
                faster than by reading two labels that both start with a capital. The short
                name stays beside it — it is the word the rest of the page, the folders and
                the grants are all written in. */}
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <SystemLogo system={system.key} label={system.label} />
              <span className="text-sm font-semibold text-[var(--text-primary)]">
                {system.label}
              </span>
              {chosen && <Badge variant="success">Selected</Badge>}
              {/* A system switched off answers nobody, and a card that looked ordinary would
                  invite somebody to file documents into a corpus no question can reach. There
                  is no control for it here — that is the Integrations tab's job — but the
                  state has to be visible where the choice is made. */}
              {row && !row.active && <Badge variant="error">Turned off</Badge>}
              {row && (
                <button
                  type="button"
                  // The card underneath is the selector; without this, editing one system
                  // would also select it, which is right, and then the card's own handler
                  // would run again on the way up. Selecting is done explicitly below.
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(system.key);
                  }}
                  // The glyph alone carries no name, so both are given: `aria-label` for a
                  // screen reader, `title` for the pointer — a pencil on a card of counts and
                  // a folder could as easily be read as "write a document".
                  aria-label={`Edit ${system.label}`}
                  title={`Edit ${system.label}`}
                  className="ml-auto inline-flex flex-none items-center justify-center rounded-[var(--radius-sm)] border border-[var(--border-light)] bg-[var(--bg-elevated)] p-1.5 text-[var(--text-secondary)] transition-colors hover:border-[var(--border)] hover:text-[var(--text-primary)]"
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              )}
            </span>
            <span className="mt-2 block text-xs leading-relaxed text-[var(--text-secondary)]">
              {system.blurb}
            </span>
            <span className="mt-2 block text-xs text-[var(--text-muted)]">
              {system.documents
                ? `${num(system.documents)} documents in ${num(system.categories)} categories`
                : 'No documents yet'}
              {/* Which directory of the repository this card is about. The corpus keeps one
                  per system, and since that folder is now a field on the system rather than
                  something derived from its key, it is the only thing on the card that says
                  where an upload will actually land. */}
              {system.folder && (
                <>
                  {' · '}
                  <code>{system.folder}/</code>
                </>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * A part of a card, with its own heading. Three of these make the one card the corpus work
 * is done in: picking the system, browsing what is filed, and adding to it are steps of a
 * single job, and three separate cards asked the reader to see them as three.
 */
function Section({
  title,
  hint,
  action,
  className,
  fill,
  children,
}: {
  title: string;
  hint?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** Hand the whole height of the row to the children rather than to white space under
   *  them. Side by side, the two sections are as tall as the taller one, and a section
   *  whose content is a list can go on down to meet it. */
  fill?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={cn(fill && 'flex min-h-0 flex-col', className)}>
      <div className="flex items-start gap-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">{title}</h3>
          {hint && (
            <p className="mt-1 max-w-prose text-xs leading-relaxed text-[var(--text-secondary)]">
              {hint}
            </p>
          )}
        </div>
        {action && <div className="ml-auto flex-none">{action}</div>}
      </div>
      <div className={cn('mt-3', fill && 'flex min-h-0 flex-1 flex-col')}>{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ browsing the corpus */

/** Fewest folders on a page. The list fills the column, and the column is as tall as the
 *  form beside it, so how many actually fit is measured rather than decided here — this is
 *  the floor, for the narrow layout where the two are stacked instead. */
const PAGE = 10;

/** One closed folder row and the rule under it, in px: `py-1.5` either side of a `text-sm`
 *  line. Only used to ask how many rows a given height holds, so a pixel out either way
 *  costs a row of white space or a row of scrolling. */
const ROW = 33;

/** Which of the two corpus folders a document was read from. */
type Origin = 'core' | 'uploads';

/**
 * The tree cut in two along the root each document came from.
 *
 * The corpus is two folders read as one — a checkout of the documentation repository, which
 * nothing on this site writes to, and the uploads folder this site does write — and drawn as
 * one list it showed the join. That made every row look equally editable when half of them
 * are not: "Replace" on a repository document does not change that document, it files a copy
 * beside it that hides it, and the repository's own copy then answers nothing. So the two
 * roots are drawn as two lists, and only the one that can be written to has buttons.
 *
 * A category with documents on both sides appears in both, holding only its own half; the
 * two counts therefore add up to the merged one. A document uploaded over a repository one
 * is in the uploads list alone — `layeredDocuments` names each path once, and the copy that
 * answers is the one it names — so the repository list is what the repository still
 * decides, not everything it contains.
 *
 * `split` is false when no document carries an origin at all. That is the deployment where
 * the corpus is not on this host and the assistant listed it instead: it does not report
 * which root a document came from, and two lists drawn from a guess would state something
 * untrue about what is editable. One undivided list is the honest answer there.
 */
function splitByOrigin(categories: CorpusCategory[]): {
  core: CorpusCategory[];
  uploads: CorpusCategory[];
  split: boolean;
} {
  const split = categories.some((category) =>
    category.documents.some((document) => document.origin),
  );
  if (!split) return { core: [], uploads: [], split: false };

  // An origin missing from one row while the others carry theirs is the repository's:
  // uploads is the root this site wrote, and the side that would have named itself.
  const side = (origin: Origin) =>
    categories
      .map((category) => ({
        ...category,
        documents: category.documents.filter((document) => (document.origin ?? 'core') === origin),
      }))
      .filter((category) => category.documents.length > 0);

  return { core: side('core'), uploads: side('uploads'), split: true };
}

/** How many documents a list of folders holds, for the count over it. */
const filed = (categories: CorpusCategory[]): number =>
  categories.reduce((total, category) => total + category.documents.length, 0);

/**
 * One of the two lists, under the heading that says which corpus it is and what may be done
 * to it. Stated here once rather than on every row: it is a fact about the whole list, and a
 * badge on each of twenty rows says it twenty times without saying it any more clearly.
 */
function Lane({
  title,
  hint,
  count,
  grow,
  children,
}: {
  title: string;
  hint: string;
  count: number;
  /** The repository list is the long one and takes the height the column has to give; the
   *  uploads list under it is as tall as what is in it. */
  grow?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex min-h-0 flex-col gap-1.5', grow && 'flex-1')}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
          {title}
        </h4>
        <span className="text-xs text-[var(--text-muted)]">
          {count === 1 ? '1 document' : `${num(count)} documents`}
        </span>
      </div>
      <p className="max-w-prose text-xs leading-relaxed text-[var(--text-muted)]">{hint}</p>
      {children}
    </div>
  );
}

/**
 * The corpus as a tree, one level deep, because that is how deep it goes: a system holds
 * subjects and a subject holds documents.
 *
 * A folder opens in place rather than replacing the list, so the other nineteen stay on
 * screen while you look inside one — and the open folder is also the upload target, which is
 * what makes "which category is this for?" a question answered by where you are standing
 * instead of by a dropdown that can disagree with what is on screen.
 *
 * The tree scrolls inside its own box. Twenty folders, plus the documents of the one that is
 * open, are more rows than the column beside this one has to give, and a card grown to eight
 * hundred pixels leaves the rest of the page staring at its own whitespace.
 */
function Browser({
  categories,
  hits,
  folder,
  onOpen,
  selected,
  onReplace,
  onDelete,
  onOpenDocument,
  readOnly = false,
  grow = true,
  empty,
}: {
  categories: CorpusCategory[];
  hits: Map<string, number>;
  folder: string | null;
  onOpen: (key: string | null) => void;
  selected: string | null;
  onReplace: (document: CorpusDocument) => void;
  onDelete: (document: CorpusDocument) => void;
  onOpenDocument: (document: CorpusDocument) => void;
  /** No Replace and no Delete: the documentation repository is not written from here, and a
   *  button that files a copy hiding a document is not the same thing as changing it. The
   *  document still opens, because reading it is the reason to browse it. */
  readOnly?: boolean;
  /** Take the whole height of the column and scroll inside it. False for a list stacked
   *  under another, which takes what it needs up to a box that leaves the other on screen. */
  grow?: boolean;
  /** What to say where there is nothing to list — the two lists are empty for different
   *  reasons, and "no documents yet" is wrong about one of them. */
  empty: string;
}) {
  const { word } = useApp();
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(PAGE);
  const box = useRef<HTMLDivElement>(null);

  // The term matches a folder or anything filed in it, so a half-remembered document name
  // finds the folder holding it rather than nothing at all.
  const needle = term.trim().toLowerCase();
  const shown = needle
    ? categories.filter(
        (category) =>
          category.label.toLowerCase().includes(needle) ||
          category.key.toLowerCase().includes(needle) ||
          category.documents.some((document) =>
            `${document.title} ${document.name}`.toLowerCase().includes(needle),
          ),
      )
    : categories;

  // A folder can be opened from outside this list — an upload lands in one, a new one is
  // named — and that folder has to be on the page you are looking at, or the tree would show
  // no open folder while the form below says it is uploading into one.
  const openIndex = shown.findIndex((category) => category.key === folder);
  useEffect(() => {
    if (openIndex >= 0) setPage(Math.floor(openIndex / rows));
    // Only when the open folder changes: paging by hand must not be undone on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder]);

  // How tall the box is depends on the form beside it and on the window, neither of which
  // this list is told about — so it is watched. Below the floor nothing changes: PAGE is what
  // a stacked, self-sized box holds anyway.
  const listed = shown.length > 0;
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height ?? 0;
      // An open folder spills its documents below the row and the box scrolls, which is what
      // it is for; the count is of closed rows either way.
      if (height > 0) setRows(Math.max(PAGE, Math.floor(height / ROW)));
    });
    observer.observe(el);
    return () => observer.disconnect();
    // The box is unmounted while a search matches nothing, so a new one has to be picked up.
  }, [listed]);

  // Growing, the list is never what makes the row tall: `h-0` lets it take the height the
  // form beside it sets and scroll inside it, and `min-h` gives it a height of its own in the
  // narrow layout, where the two are stacked and there is nothing beside it to match. Not
  // growing — the uploads list, under the repository's — it is as tall as what is in it, up
  // to a ceiling that keeps the list above it on screen.
  const shell = cn(
    'divide-y divide-[var(--border-light)] overflow-y-auto rounded-[var(--radius-md)] border border-[var(--border-light)]',
    grow ? 'h-0 min-h-[20rem] flex-1' : 'max-h-[16rem]',
  );
  const row =
    'flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-[var(--bg-secondary)]';

  // No early return for an empty list: a folder just named is in no list yet, and the line
  // that says so is above the box rather than instead of it.
  // Clamped rather than reset, since a narrower filter — or a page that now holds more
  // folders than it did — can leave the reader on a page that no longer exists.
  const pages = Math.max(1, Math.ceil(shown.length / rows));
  const current = Math.min(page, pages - 1);
  const first = current * rows;
  const slice = shown.slice(first, first + rows);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {/* Only above a list long enough to be worth filtering — under a dozen folders the eye
          is faster than the box. */}
      {categories.length > 8 && (
        <Input
          placeholder={word('Find a folder or a document', 'Search folders')}
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            // A new term is a new list, and page three of the old one means nothing in it.
            setPage(0);
          }}
          leftIcon={<Search className="h-4 w-4" />}
          size="sm"
          aria-label="Find a folder"
        />
      )}

      {/* A folder named but not on disk yet has no entry in the tree until something is filed
          in it, so it is said above the tree rather than instead of it. */}
      {folder && !categories.some((category) => category.key === folder) && (
        <p className="rounded-[var(--radius-md)] border border-dashed border-[var(--border)] px-3 py-2 text-sm text-[var(--text-muted)]">
          New folder — it is created when the first document lands in it.
        </p>
      )}

      {!shown.length ? (
        <p className="rounded-[var(--radius-md)] border border-dashed border-[var(--border)] px-3 py-6 text-center text-sm text-[var(--text-muted)]">
          {needle ? `No folder or document here matches “${term.trim()}”.` : empty}
        </p>
      ) : (
        <div ref={box} className={shell}>
          {slice.map((category) => {
            const isOpen = category.key === folder;
            const Chevron = isOpen ? ChevronDown : ChevronRight;

            return (
              <div key={category.key}>
                {/* One click opens the folder and aims the upload at it; the same click on an
                    open folder closes it again and leaves the upload without a target, which
                    is the honest state rather than a stale one. */}
                <button
                  type="button"
                  aria-expanded={isOpen}
                  className={cn(row, isOpen && 'bg-[var(--primary-tint)]')}
                  onClick={() => onOpen(isOpen ? null : category.key)}
                >
                  <Chevron className="h-3.5 w-3.5 flex-none text-[var(--text-muted)]" aria-hidden />
                  <Folder
                    className={cn(
                      'h-4 w-4 flex-none',
                      isOpen ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]',
                    )}
                    aria-hidden
                  />
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate text-sm text-[var(--text-primary)]',
                      isOpen && 'font-medium',
                    )}
                  >
                    {category.label}
                  </span>
                  <span className="flex-none text-xs text-[var(--text-muted)]">
                    {num(category.documents.length)}
                  </span>
                </button>

                {isOpen && (
                  <div className="divide-y divide-[var(--border-light)] border-t border-[var(--border-light)]">
                    {category.documents.map((document) => (
                      <div
                        key={document.source}
                        // Indented past the chevron, so the documents read as the contents of
                        // the folder above them rather than as more folders.
                        className={cn(
                          'flex flex-wrap items-center gap-x-3 gap-y-1 py-2 pl-9 pr-3',
                          document.source === selected && 'bg-[var(--primary-light)]',
                        )}
                      >
                        <FileText
                          className="h-4 w-4 flex-none text-[var(--text-muted)]"
                          aria-hidden
                        />
                        {/* The name is the way in, the way a file name is in any file list.
                            Everything else about the row is metadata; the document is the
                            thing. */}
                        <span className="min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={() => onOpenDocument(document)}
                            className="block max-w-full truncate text-left text-sm text-[var(--text-primary)] hover:text-[var(--primary)] hover:underline"
                          >
                            {document.title}
                          </button>
                          <span className="block truncate text-xs text-[var(--text-muted)]">
                            {document.name} · {document.size_kb} kB ·{' '}
                            {word(
                              `${num(hits.get(document.source) ?? 0)} retrievals`,
                              `used ${num(hits.get(document.source) ?? 0)}×`,
                            )}
                            {/* Only the case that changes what an action does is worth a
                                row: a copy edited here answers instead of the repository's,
                                so updating that document in the repository will look like
                                it did nothing. A document straight from the repository is
                                the ordinary case and says nothing extra. */}
                            {document.shadows_core
                              ? ' · edited here, hiding the repository copy'
                              : ''}
                          </span>
                        </span>
                        {/* Nothing on a repository row — not a disabled button, which is a
                            control that says "later"; there is no later. What may be done to
                            these is said once, over the list. */}
                        {!readOnly && (
                          <span className="flex flex-none items-center gap-1">
                            <Button size="sm" variant="outline" onClick={() => onReplace(document)}>
                              {document.source === selected ? 'Replacing' : 'Replace'}
                            </Button>
                            {/* Icon-only, and last: replacing is the everyday action and
                                deleting is the one there is no undo for, so it does not sit
                                under the cursor on the way to the other. What it would remove
                                is named in the confirmation, not here. */}
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`Delete ${document.title}`}
                              title="Delete"
                              onClick={() => onDelete(document)}
                            >
                              <Trash2 className="h-4 w-4 text-[var(--error)]" aria-hidden />
                            </Button>
                          </span>
                        )}
                      </div>
                    ))}

                    {!category.documents.length && (
                      <p className="py-4 pl-9 pr-3 text-sm text-[var(--text-muted)]">
                        This folder is empty.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* As many folders at a time as the column is tall enough to show, and the rest a step
          away. Which of them you are on is said in folders rather than in page numbers,
          because "11–20 of 23" answers the question a page number only stands for. */}
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs text-[var(--text-muted)]">
          <span>
            {word(
              `${num(first + 1)}–${num(first + slice.length)} of ${num(shown.length)} folders`,
              `folders ${num(first + 1)}–${num(first + slice.length)} of ${num(shown.length)}`,
            )}
          </span>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Previous folders"
            disabled={current === 0}
            onClick={() => setPage(current - 1)}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label="More folders"
            disabled={current >= pages - 1}
            onClick={() => setPage(current + 1)}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      )}
    </div>
  );
}

/** Where you are, and the way back out. Two levels, so it is a sentence rather than a trail. */
function Breadcrumb({
  system,
  label,
  onOpen,
}: {
  system: string;
  label: string | null;
  onOpen: (key: string | null) => void;
}) {
  return (
    <p className="flex items-center gap-1.5 text-sm">
      <button
        type="button"
        onClick={() => onOpen(null)}
        className={cn(
          'font-medium',
          label ? 'text-[var(--primary)] hover:underline' : 'text-[var(--text-primary)]',
        )}
      >
        {system}
      </button>
      {label && (
        <>
          <span className="text-[var(--text-muted)]">/</span>
          <span className="font-medium text-[var(--text-primary)]">{label}</span>
        </>
      )}
    </p>
  );
}

/**
 * A folder is a directory on disk, so there is nothing to create until something is put in
 * it: naming one here just opens it, and the first upload is what makes it exist.
 */
function NewFolder({ onCreate }: { onCreate: (key: string) => void }) {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  // The same slug the assistant makes of it, so the name shown here is the folder that turns
  // up on disk rather than a prettier one that then fails to match.
  const key = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

  function create() {
    if (!key) return;
    onCreate(key);
    setNaming(false);
    setName('');
  }

  if (!naming) {
    return (
      <Button type="button" size="sm" variant="ghost" onClick={() => setNaming(true)}>
        <FolderPlus className="mr-1.5 h-4 w-4" aria-hidden />
        New folder
      </Button>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <Input
        value={name}
        autoFocus
        placeholder="e.g. onboarding"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            create();
          }
          if (e.key === 'Escape') setNaming(false);
        }}
        className="w-[180px]"
      />
      <Button type="button" size="sm" disabled={!key} onClick={create}>
        Create
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={() => setNaming(false)}>
        Cancel
      </Button>
    </span>
  );
}

/**
 * A document, read the way the assistant reads it — and edited in place.
 *
 * Reading shows the sections it is cut into rather than one page of prose, because a section
 * is the unit that comes back as an excerpt: what is on screen is what can be quoted into an
 * answer. Markdown is left as written; prettifying it would hide the one thing worth
 * checking, which is whether the headings the chunker relies on are actually there.
 *
 * Each section says who can reach it, because the heading is what decides that: one that says
 * whose steps follow — “Finance responsibilities” — is retrievable by finance and by nobody
 * else, and one that claims nobody belongs to the document. That is a consequence of the
 * wording, and renaming a heading changes it, so it is shown rather than left to be inferred.
 *
 * Editing is the same idea from the other side. A typo, a changed deadline or a wrong phone
 * number is most of what a corpus needs, and re-exporting a PDF to fix one line is the reason
 * such a line stays wrong. What it cannot touch is the front matter: the title and the
 * folder are decided where the document is filed, not by typing into its text.
 */
function DocumentViewer({
  system,
  document,
  onClose,
  onReplace,
}: {
  system: string;
  document: CorpusDocument | null;
  onClose: () => void;
  onReplace: (document: CorpusDocument) => void;
}) {
  const { reload } = useApp();
  const query = useApi<CorpusText>(
    '/api/corpus/document',
    { system, source: document?.source ?? '' },
    Boolean(document),
  );

  /** The edited body, or null when reading. Not seeded from `query.data` on every render —
   *  that would throw away what was typed each time the panel re-asked. */
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function close() {
    setDraft(null);
    setError('');
    onClose();
  }

  async function save() {
    if (draft === null || !document) return;
    setBusy(true);
    setError('');
    const { ok, data } = await write<UploadResult & { detail?: string }>('/api/corpus/upload', {
      system,
      target: document.source,
      text: draft,
    });
    setBusy(false);
    if (!ok) {
      setError(detailOf(data, 'Could not save.'));
      return;
    }
    close();
    reload();
  }

  const editing = draft !== null;

  return (
    <Modal
      isOpen={Boolean(document)}
      onClose={close}
      size="xl"
      title={document?.title ?? ''}
      description={document?.source ?? ''}
    >
      {document && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--text-muted)]">
            <Badge>{document.category.replace(/[_-]+/g, ' ')}</Badge>
            {/* Who reaches this document — one grant, for the whole file, read straight from
                its front matter. Shown here rather than beside each section because that is
                where it is decided: retrieval clears or refuses the document, not the
                heading. A document granting nobody is drawn as a warning, since its only
                other symptom is silence. */}
            <Badge variant={document.allowed_roles.length ? 'default' : 'warning'}>
              {document.allowed_roles.length
                ? `${document.allowed_roles.length} role${document.allowed_roles.length === 1 ? '' : 's'}`
                : 'readable by nobody'}
            </Badge>
            {/* Which of the two corpus folders this text came from. It decides what saving
                does: a repository document is never written to, so an edit is stored here as
                a copy that hides it — and from then on the repository's version of that
                document changes nothing until the copy is deleted. */}
            {document.origin === 'uploads' ? (
              <Badge variant={document.shadows_core ? 'warning' : 'default'}>
                {document.shadows_core ? 'uploaded — hides the repository copy' : 'uploaded here'}
              </Badge>
            ) : document.origin === 'core' ? (
              <Badge>from the documentation repository</Badge>
            ) : null}
            <span>{document.size_kb} kB</span>

            <span className="ml-auto flex flex-none items-center gap-2">
              {editing ? (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setDraft(null)} disabled={busy}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={save} isLoading={busy}>
                    {busy ? 'Saving and re-indexing…' : 'Save and re-index'}
                  </Button>
                </>
              ) : document.origin === 'core' ? (
                // The repository's own copy, open to be read. Neither button is offered,
                // because neither would do what its label says: this file is not written from
                // here, and saving or replacing it files a copy in uploads that hides it — a
                // different document, at the same path, that then answers instead. The
                // sentence says where the change is actually made.
                <span className="text-xs text-[var(--text-muted)]">
                  Read-only — edit this in the documentation repository.
                </span>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!query.data}
                    onClick={() => setDraft(query.data?.content ?? '')}
                  >
                    <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      onReplace(document);
                      close();
                    }}
                  >
                    Replace file
                  </Button>
                </>
              )}
            </span>
          </div>

          {(query.error || error) && (
            <p className="rounded-[var(--radius-md)] border border-[var(--error)] bg-[var(--error-light)] px-3 py-2 text-sm">
              {error || query.error?.message}
            </p>
          )}

          {!query.data && !query.error && <SkeletonText lines={6} />}

          {query.data && editing && (
            <>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
                className="h-[55vh] w-full rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-primary)] p-3 font-mono text-xs leading-relaxed text-[var(--text-primary)] focus-visible:outline-2 focus-visible:outline-[var(--primary)]"
              />
              <p className="text-xs text-[var(--text-muted)]">
                Markdown. The <code>##</code> headings are where the assistant cuts the
                document into the passages it retrieves. Saving rewrites the file and rebuilds
                the index.
              </p>
            </>
          )}

          {query.data && !editing && (
            <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
              {query.data.sections.map((section, index) => (
                <section key={`${section.heading}-${index}`}>
                  {/* The heading alone. Access is a property of the document, not of the
                      section — it is stated once, above — so there is nothing per-section to
                      say about who reads this. */}
                  {section.heading ? (
                    <h4 className="text-sm font-semibold text-[var(--text-primary)]">
                      {section.heading}
                    </h4>
                  ) : (
                    <h4 className="text-sm font-semibold text-[var(--text-muted)]">
                      Opening (no heading)
                    </h4>
                  )}
                  {/* Pre-wrapped rather than rendered: the whitespace and the `#` marks are
                      part of what the chunker sees. */}
                  <pre className="mt-1 whitespace-pre-wrap break-words rounded-[var(--radius-md)] bg-[var(--bg-secondary)] p-3 text-xs leading-relaxed text-[var(--text-secondary)]">
                    {section.text || '(empty)'}
                  </pre>
                </section>
              ))}
              {!query.data.sections.length && (
                <p className="text-sm text-[var(--text-muted)]">
                  This document has no text under any heading, so nothing in it can be
                  retrieved.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * Deleting is the one action on this page that cannot be undone from this page, so it says
 * what will stop being answerable before it happens — the document's name, and how much work
 * it has been doing. A file that answered nothing and one that answered a hundred questions
 * look identical in a list; only one of them is safe to remove without thinking.
 */
function DeleteDialog({
  system,
  document,
  hits,
  onClose,
}: {
  system: string;
  document: CorpusDocument | null;
  hits: number;
  onClose: () => void;
}) {
  const { reload } = useApp();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function confirm() {
    if (!document) return;
    setBusy(true);
    setError('');
    const { ok, data } = await write<DeleteResult & { detail?: string }>('/api/corpus/delete', {
      system,
      target: document.source,
    });
    setBusy(false);
    if (!ok) {
      setError(detailOf(data, 'Delete failed.'));
      return;
    }
    onClose();
    reload();
  }

  return (
    <>
      <ConfirmModal
        isOpen={Boolean(document)}
        onClose={onClose}
        onConfirm={confirm}
        variant="danger"
        title="Delete this document?"
        confirmLabel="Delete and re-index"
        isLoading={busy}
        message={
          !document
            ? ''
            : document.shadows_core
              ? // Not a deletion at all: the copy uploaded here goes, and the repository's
                // version of the same document starts answering again. Said as what happens,
                // because "delete" would describe the wrong outcome.
                `“${document.title}” (${document.source}) goes back to the version in the ` +
                `documentation repository, and the index is rebuilt. The document keeps ` +
                `answering — from the repository's text, not the one edited here. It has ` +
                `been retrieved ${hits} time${hits === 1 ? '' : 's'}.`
              : `“${document.title}” (${document.source}) is removed from the corpus and the ` +
                `index is rebuilt, so the assistant stops answering from it. It has been ` +
                `retrieved ${hits} time${hits === 1 ? '' : 's'}. The file is deleted from ` +
                `disk — recover it from version control if this was a mistake.`
        }
      />
      {error && (
        <p className="mt-2 text-sm text-[var(--error)]" role="alert">
          {error}
        </p>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ the form */

/**
 * A file, and nothing else. The route still accepts pasted Markdown via `text` and
 * `filename`, unset from here, but the form does not offer it.
 *
 * What the file becomes is decided by where the browser is: inside a folder it is a new
 * document of that subject; with a document picked it is a new revision of that document.
 */
function UploadForm({
  system,
  folder,
  folderLabel,
  target,
  onTargetChange,
  onUploaded,
}: {
  system: string;
  /** Category key the upload is filed under, or null at the top level. */
  folder: string | null;
  folderLabel: string | null;
  target: CorpusDocument | null;
  onTargetChange: (document: CorpusDocument | null) => void;
  onUploaded: (category: string) => void;
}) {
  const { word, reload } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; node: React.ReactNode } | null>(null);
  /** The roles this document will be readable by. Empty is not a default — it is the state
   *  the form refuses to submit from, because a document granting nothing answers nobody. */
  const [roles, setRoles] = useState<string[]>([]);

  // Asked of the server, which asks the assistant: the role names are the assistant's enum,
  // and a picker with its own copy would offer roles that uploads then get rejected for.
  const vocabulary = useApi<RoleVocabulary>('/api/corpus/roles', { system }, Boolean(system));

  // The mode follows the browser rather than a separate control: opening a folder and picking
  // a document are the whole of the decision, and a switch that could disagree with them
  // would be a second place for the same answer to live.
  const replacing = Boolean(target);

  // Picking a document to replace loads the roles it already grants, so the common case —
  // a new revision of the same document, for the same people — needs no decision at all,
  // and any change to it is visibly a change.
  useEffect(() => {
    setRoles(target ? [...(target.allowed_roles ?? [])] : []);
  }, [target?.source]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setResult(null);

    const { ok, data } = await write<UploadResult & { detail?: string }>('/api/corpus/upload', {
      system,
      title: title || null,
      category: replacing ? null : folder,
      target: target?.source ?? null,
      // Left empty when replacing and nothing was picked: the assistant keeps the roles the
      // document already had, which is what a revision of it should do.
      allowed_roles: roles.length ? roles.join(',') : null,
      file,
    });

    setBusy(false);
    if (!ok) {
      setResult({ ok: false, node: detailOf(data, 'Upload failed.') });
      return;
    }

    // The assistant returns the stored path as `saved`. The chunk count matters only to
    // whoever tunes the indexing, so the simple view confirms the file instead.
    setResult({
      ok: true,
      node: (
        <>
          <code className="rounded bg-[var(--bg-tertiary)] px-1.5 py-0.5 text-xs">
            {data.saved || ''}
          </code>
          <span className="text-[var(--text-muted)]">
            {data.replaced
              ? word(
                  `replaced · ${num(data.chunks_indexed)} chunks indexed`,
                  'replaced — the assistant answers from the new version now',
                )
              : word(
                  `${num(data.chunks_indexed)} chunks indexed`,
                  'the assistant can answer from it now',
                )}
          </span>
        </>
      ),
    });
    setFile(null);
    setTitle('');
    // The roles are deliberately kept: documents arrive in batches for the same set of
    // readers, and re-picking them per file is how the wrong one gets granted.
    onTargetChange(null);
    // Stay in the folder the document landed in — which, for a folder named a moment ago, is
    // one the browser has not heard of until this reload.
    if (data.category) onUploaded(data.category);
    reload();
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {replacing ? (
        /* What is about to be overwritten, named. An override is the one action on this page
         * that destroys something, and the file being dropped in says nothing about which
         * document it lands on. */
        <div className="rounded-[var(--radius-md)] border border-[var(--primary)] bg-[var(--primary-light)] p-3">
          <div className="flex items-start gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-[var(--text-primary)]">
                Replacing “{target!.title}”
              </p>
              <p className="mt-0.5 truncate text-xs text-[var(--text-muted)]">{target!.source}</p>
              <p className="mt-1 text-xs text-[var(--text-secondary)]">
                The new file takes its place and is indexed at once, and stays in this folder.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="ml-auto flex-none"
              onClick={() => onTargetChange(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[var(--text-secondary)]">
          {folder ? (
            <>
              Filing into <b className="text-[var(--text-primary)]">{folderLabel}</b>.
            </>
          ) : (
            'Open a folder above, or make one, to say what the document is about.'
          )}
        </p>
      )}

      {/* Above the dropzone, not below it: the file is the last thing chosen, and a field
          under a filled dropzone reads as something that happened to the file already
          picked rather than as part of the same form. */}
      <Input
        label={replacing ? 'Title (optional — keeps the current one)' : 'Title (optional)'}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={replacing ? target!.title : 'e.g. Visitor Registration Procedure'}
      />

      <div>
        <span className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">
          {word('File (PDF, Word, Markdown or text)', 'File (PDF, Word or text)')}
        </span>
        <FileSelectDropzone
          selectedFile={file}
          onSelect={setFile}
          onRemove={() => setFile(null)}
          acceptedTypes={['pdf', 'docx', 'md', 'markdown', 'txt']}
          maxSizeMb={MAX_UPLOAD_MB}
        />
      </div>

      {/* Who may read it. Last, because it is the field to think about with the document in
          front of you — and unskippable, because a document filed with no grant is indexed,
          looks uploaded, and answers nobody. */}
      <RolesField
        vocabulary={vocabulary.data}
        loading={vocabulary.loading}
        error={vocabulary.error}
        selected={roles}
        onChange={setRoles}
        inherited={replacing ? (target!.allowed_roles ?? []) : null}
      />

      {/* Without a file there is nothing to send, and the refusal would come back from the
          assistant as a sentence about a missing field. Without a folder the document would
          be filed as "not yet categorised", which is a place to put something deliberately
          rather than to arrive at by leaving a control alone. Without a role it would be
          retrievable by nobody, which is the one mistake here that looks like success. */}
      <Button
        type="submit"
        className="w-full"
        isLoading={busy}
        disabled={!system || !file || (!replacing && !folder) || !roles.length}
      >
        {busy
          ? replacing
            ? 'Replacing and re-indexing…'
            : 'Uploading and re-indexing…'
          : replacing
            ? 'Replace and re-index'
            : folderLabel
              ? `Upload into ${folderLabel}`
              : 'Upload and re-index'}
      </Button>

      {result && <ResultLine ok={result.ok}>{result.node}</ResultLine>}
    </form>
  );
}

/**
 * Who may read the document being uploaded — the field that decides whether it ever appears
 * in an answer, and for whom.
 *
 * Every role is a checkbox rather than a multi-select, because this is the one field on the
 * form that cannot be corrected by uploading again: by the time somebody notices, the
 * document is indexed and has been answering. The two shortcuts come from the server with
 * what they expand to, so this component never has an opinion about what "everyone" means.
 */
function RolesField({
  vocabulary,
  loading,
  error,
  selected,
  onChange,
  /** What the document being replaced already grants, or null when filing a new one. */
  inherited,
}: {
  vocabulary: RoleVocabulary | undefined;
  loading: boolean;
  error: Error | undefined;
  selected: string[];
  onChange: (roles: string[]) => void;
  inherited: string[] | null;
}) {
  const shortcuts = vocabulary?.shortcuts ?? [];
  const matched = shortcuts.find(
    (shortcut) =>
      shortcut.expands_to.length === selected.length &&
      shortcut.expands_to.every((role) => selected.includes(role)),
  );
  const unchanged =
    inherited !== null &&
    inherited.length === selected.length &&
    inherited.every((role) => selected.includes(role));

  const options = (vocabulary?.roles ?? []).map((role) => ({
    value: role.value,
    label: role.label,
  }));

  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium text-[var(--text-primary)]">
        Who may read it
      </span>
      <p className="mb-2 text-xs text-[var(--text-muted)]">
        Only these roles will ever retrieve this document. The filter runs inside the search,
        so an excerpt nobody here is granted is never shown to the model at all.
      </p>

      {/* Three states, and each of them says which one it is. An empty box would be the
          worst of the three: it reads as "this document has no roles to choose from", when
          what actually happened is that the list could not be fetched — and it disables the
          upload button with no way to find out why. */}
      {loading && !vocabulary ? (
        <SkeletonText lines={3} />
      ) : error || !vocabulary?.roles?.length ? (
        <div className="rounded-[var(--radius-md)] border border-[var(--danger)] bg-[var(--danger-light,var(--bg-secondary))] p-3 text-xs leading-relaxed text-[var(--text-secondary)]">
          <p className="font-medium text-[var(--text-primary)]">
            The roles could not be loaded, so this document cannot be uploaded yet.
          </p>
          <p className="mt-1">
            The list of roles comes from the assistant, which owns it. Check that the
            assistant is running and reachable at the URL this service is configured with —
            if it was restarted recently, give it a moment and reload this page.
          </p>
          {error && (
            <p className="mt-1 font-mono text-[var(--text-muted)]">{error.message}</p>
          )}
        </div>
      ) : (
        <>
          {/* One field, not a wall of checkboxes. There are 32 roles now and there will be
              more the next time EIS gains a profile; laid out as a grid they cost most of
              the form's height and still have to be read three columns at a time to answer
              "is finance ticked?". A dropdown with a search box answers that by typing, and
              the field itself shows the current grant when it is closed.
              `showSelectAll` covers "everyone signed in", so the shortcut buttons are gone
              with the grid — one control does both jobs. */}
          <MultiSelectDropdown
            value={selected}
            options={options}
            onChange={(values) => onChange(values.map(String))}
            placeholder="Choose the roles that may read this document"
            searchPlaceholder="Search roles…"
            showSelectAll
            selectAllLabel={shortcuts[0]?.label ?? 'Everyone signed in'}
            maxDisplayItems={4}
          />

          <p
            className={cn(
              'mt-2 text-xs',
              selected.length ? 'text-[var(--text-muted)]' : 'text-[var(--danger)]',
            )}
          >
            {!selected.length
              ? 'No roles chosen — this document would be readable by nobody, so the upload is refused.'
              : unchanged
                ? `Keeping the ${selected.length} role${selected.length === 1 ? '' : 's'} this document already grants.`
                : matched
                  ? `Readable by ${matched.label.toLowerCase()}.`
                  : `Readable by ${selected.length} role${selected.length === 1 ? '' : 's'}: ${selected.join(', ')}.`}
          </p>
        </>
      )}
    </div>
  );
}

/** Only useful to somebody with a shell on the machine, which an admin is not. */
function ReindexForm({ system }: { system: string }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage('');
    const { ok, data } = await write<{ chunks_indexed?: number }>('/api/corpus/reindex', { system });
    setBusy(false);
    setMessage(ok ? `${num(data.chunks_indexed)} chunks indexed.` : detailOf(data, 'Re-index failed.'));
  }

  return (
    <form onSubmit={submit} className="mt-6 border-t border-[var(--border-light)] pt-5">
      <h3 className="text-sm font-semibold text-[var(--text-primary)]">Re-index without uploading</h3>
      {/* No path. It used to name `docs/rag`, which is this repository's default and not
          where a given host reads from, and naming the resolved folders instead put the
          server's absolute paths on screen for every operator. The sentence is true of both
          corpus folders without pointing at either. */}
      <p className="mt-1 text-sm text-[var(--text-secondary)]">
        Use this after editing the corpus on the server by hand.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button type="submit" variant="outline" isLoading={busy} disabled={!system}>
          Re-index now
        </Button>
        {message && <span className="text-sm text-[var(--text-muted)]">{message}</span>}
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ the page */

export function DocumentsPanel() {
  const { days, system: scope, me, simple, word } = useApp();
  /* Whether the documentation repository's half of the corpus is drawn at all. Only for a
   * superadmin: it is read-only here — changed in the repository and re-indexed, never from
   * this page — so for an admin it was ninety rows to scroll past on the way to the few
   * they can actually write. The server leaves them out of the tree for the same accounts,
   * so this decides what the page draws, not what it is allowed to know. */
  const showCore = me.is_superadmin;
  const corpus = useApi<CorpusSystems>('/api/corpus/systems');

  /* The same systems as rows, for the account allowed to change them. Two requests for one
   * list, deliberately: `/api/corpus/systems` answers what the picker draws — a name, a line
   * and how much is filed — and is read by every account, while this one carries the id, the
   * on/off flag and how many integrations point at each, and the route refuses anybody but a
   * superadmin. Merging them would put a superadmin-only shape on a route an admin calls.
   *
   * Deliberately NOT in the `settled` gate below: it does not exist for an admin, and a page
   * that waited for it would never draw for one. */
  const manageable = useApi<{ items: System[] }>('/api/systems', {}, me.is_superadmin);
  const [adding, setAdding] = useState(false);
  /** Which system's card had its Edit pressed. A key rather than a boolean, so pressing Edit
   *  on a second card while the first is open swaps the form instead of leaving it showing
   *  the system that is no longer selected. */
  const [editingKey, setEditingKey] = useState<string | null>(null);

  /* Which corpus is being written to. Its own state, not the toolbar's: the toolbar narrows
   * what the numbers are about and can be "all systems", which is not somewhere a document
   * can be filed. Preselected from the toolbar when that names one, so arriving here having
   * already chosen a system does not ask again. */
  const [chosen, setChosen] = useState<string | null>(null);
  const available = corpus.data?.systems ?? [];
  const system =
    (chosen && available.some((s) => s.key === chosen) && chosen) ||
    (typeof scope === 'string' && available.some((s) => s.key === scope) ? scope : null) ||
    available[0]?.key ||
    '';

  /** The folder open in the browser, and the document being replaced. Both belong to one
   *  system, so both are cleared when the system changes. */
  const [folder, setFolder] = useState<string | null>(null);
  /** Which of the two lists that folder is open in. The same folder name exists on both
   *  sides — a subject the repository documents and this site has also uploaded into — and
   *  without this, one click opened it in both. */
  const [openIn, setOpenIn] = useState<Origin>('uploads');
  const [target, setTarget] = useState<CorpusDocument | null>(null);
  /** The document the confirmation is asking about, if any. */
  const [doomed, setDoomed] = useState<CorpusDocument | null>(null);
  /** The document open in the reader, if any. */
  const [reading, setReading] = useState<CorpusDocument | null>(null);

  const tree = useApi<CorpusTree>('/api/corpus/tree', { system }, Boolean(system));
  // Repeated here rather than linked to, so the thing you are writing a document *for* is
  // on screen while you write it.
  const gaps = useApi<GapsResponse>('/api/gaps', { days, system: scope, limit: 10 });
  // Deliberately not filtered by the period: the two weeks either side of a change are the
  // window that matters, and they are fixed by the change's own date.
  const impact = useApi<{ items: ImpactItem[] }>('/api/impact', { window_days: 14, system: scope });
  const coverage = useApi<Coverage>('/api/coverage', { days, system: scope });

  /** How often each document was actually retrieved, so the browser says which entries are
   *  load-bearing — the ones worth being careful about replacing. */
  const hits = useMemo(
    () => new Map((coverage.data?.documents ?? []).map((d) => [d.source, d.hits])),
    [coverage.data],
  );

  const categories = tree.data?.categories ?? [];
  /** The same tree, cut along the root each document came from — what the repository decides
   *  and what this site does. See `splitByOrigin`. */
  const { core, uploads, split } = useMemo(() => splitByOrigin(categories), [categories]);
  const openFolder = categories.find((category) => category.key === folder);
  /* A folder named but not yet on disk has no entry in the tree, so its label is the name
   * that was typed, humanised the way the server humanises an unknown folder — otherwise the
   * name would change the moment the first upload lands and the server starts naming it. */
  const folderLabel =
    openFolder?.label ??
    (folder ? folder.replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase()) : null);
  const systemLabel = available.find((s) => s.key === system)?.label ?? system;
  /* The row behind the chosen card, when there is one to change. A system the picker offers
   * can have no row — a folder on disk, or traffic in the telemetry, that nobody has
   * declared — and there is nothing to edit about one of those, so no controls are drawn. */
  const chosenRow = manageable.data?.items.find((s) => s.system_key === system);
  /** The declared systems by key, for the cards. Empty for an admin, who never fetches them. */
  const systemRows = new Map((manageable.data?.items ?? []).map((s) => [s.system_key, s]));
  /* Only while its own card is still the chosen one: deleting a system elsewhere, or picking
   * another card, closes the form rather than leaving it editing something off screen. */
  const editing = editingKey === system ? chosenRow : undefined;

  /** `where` is the list the folder was opened in. It defaults to uploads because every
   *  other way of arriving at a folder — naming one, or an upload landing in it — is a way
   *  of arriving at an uploads folder. */
  function goTo(key: string | null, where: Origin = 'uploads') {
    setFolder(key);
    setOpenIn(where);
    setTarget(null);
  }

  /* Four queries, one per card, and this is the only panel with more than one: the others
   * cannot do what this one did, which was to arrive a card at a time and move everything
   * below each arrival. So it waits as an empty well — the way `Async`'s page shape does —
   * until nothing is outstanding, and is then drawn once, in its final shape.
   *
   * A query that failed counts as settled: its own card says so where it stands, and one
   * dead endpoint must not hold back the three that answered. Later windows do not wait at
   * all, since `useApi` keeps the previous answer while it re-asks.
   */
  const settled = [corpus, gaps, impact, coverage].every(
    (query) => query.data !== undefined || query.error,
  );
  if (!settled) return null;

  return (
    <div className="space-y-5">
      {/* No corpus tiles here. The page opens on the job — the corpus, and the form that adds
          to it — and the same counts are in the cards at the foot of the page, beside the
          documents they are counts of. */}
      {/* One card, three sections. Picking the system, browsing what is filed and adding to it
          are steps of a single job, and three cards asked the reader to see them as three —
          but stacked inside one column they made a card twice the height of anything that
          could sit beside it. So the steps are divided by rules rather than by cards: the
          picker across the top, then the corpus and the form for it side by side. */}
      {/* No explanation on the card itself: each section below says what it is for, and a
          fourth sentence above them only repeated the three. */}
      {/* The explanation the removed section heading used to carry, moved up to the card's
          own title: everything inside follows the system picked at the top. */}
      <PanelCard
        title="System"
        hint="Pick a system to see the documents filed under it, add new ones, and read what its users asked but were not answered. A system owns one folder of the documentation repository, and everything filed in that folder belongs to it."
        simpleHint="Choose a system to see and add its documents."
        // Adding a system belongs here rather than on a tab of its own: the list it adds to is
        // the picker directly below, and a system is made in order to file documents under it.
        // Superadmin only, and `/api/systems` refuses anybody else — an admin runs one
        // system's documents, not the list of systems.
        action={
          me.is_superadmin &&
          !adding && (
            <Button size="sm" onClick={() => setAdding(true)}>
              Add a system
            </Button>
          )
        }
      >
        <Async query={corpus}>
          {(data) =>
            !isCurrentShape(data.systems) ? (
              <p className="rounded-[var(--radius-md)] border border-[var(--error)] bg-[var(--error-light)] px-4 py-3 text-sm text-[var(--text-primary)]">
                The analytics service is running an older build than this page — it still
                answers <code>/api/corpus/systems</code> with a plain list of names. Restart it
                to pick up the current routes.
              </p>
            ) : !data.systems.length && !adding ? (
              <p className="text-sm text-[var(--text-muted)]">
                {me.is_superadmin
                  ? 'No system exists yet, and no corpus folder is visible from this host — so there is nothing to upload into. Add one above.'
                  : 'No corpus folder is visible from this host, and ANALYTICS_SYSTEMS is unset — so there is no system to upload into.'}
              </p>
            ) : (
              <div>
                {adding && (
                  <div className="mb-4">
                    <AddSystemForm
                      onDone={(key) => {
                        setAdding(false);
                        // Selected on the way out, so the corpus below is the one just made
                        // rather than whichever card happens to sort first.
                        if (key) {
                          setChosen(key);
                          goTo(null);
                        }
                      }}
                    />
                  </div>
                )}

                {/* No heading over the picker: the card is named for it, and a second
                    "System" directly under that title only said it twice. */}
                <SystemPicker
                  systems={data.systems}
                  value={system}
                  onChange={(key) => {
                    setChosen(key);
                    setEditingKey(null);
                    // Both belong to the corpus they were picked from; carrying them across
                    // would aim an upload at another system's folder.
                    goTo(null);
                  }}
                  rows={systemRows}
                  onEdit={(key) => {
                    // Editing a card selects it too. The form is about one system and so is
                    // everything below it, and a page editing one while browsing another
                    // would be two answers to the same question.
                    if (key !== system) {
                      setChosen(key);
                      goTo(null);
                    }
                    setEditingKey((open) => (open === key ? null : key));
                  }}
                />

                {/* Full width, under the grid. A card is about eighteen rems wide: a place to
                    put a button, not a place to put four fields. */}
                {editing && (
                  <div className="mt-4">
                    <EditSystemForm item={editing} onDone={() => setEditingKey(null)} />
                  </div>
                )}

                {/* The rule between them is the whole point of the two columns: what is filed
                    on the left, what writes into it on the right, one job either side of a
                    line rather than two cards with a gap between them. */}
                <div className="mt-6 grid gap-6 border-t border-[var(--border-light)] pt-6 xl:grid-cols-2 xl:gap-8">
                  <Section
                    fill
                    title={word('What is filed', 'Your folders')}
                    // Only two lists have a second one to name.
                    hint={
                      showCore
                        ? 'Open a folder to see what is in it. The form beside this writes into the folder you open — always into the first list.'
                        : word(
                            'Open a folder to see what is in it. The form beside this writes into the folder you open.',
                            'Open a folder to see what is inside. Whatever you upload goes into the folder you opened.',
                          )
                    }
                    action={<NewFolder onCreate={goTo} />}
                  >
                    <div className="flex min-h-0 flex-1 flex-col gap-3">
                      <Breadcrumb system={systemLabel} label={folderLabel} onOpen={goTo} />

                      <Async query={tree}>
                        {() =>
                          !split || !showCore ? (
                            // One list. Either no origins were reported — the corpus is not
                            // on this host and the assistant listed it, see `splitByOrigin`
                            // — or this account is not shown the repository's half, and what
                            // is left is the one list it can write.
                            <Browser
                              categories={split ? uploads : categories}
                              hits={hits}
                              folder={folder}
                              onOpen={goTo}
                              selected={target?.source ?? null}
                              onReplace={setTarget}
                              onDelete={setDoomed}
                              onOpenDocument={setReading}
                              empty={word(
                                'This system has no documents yet. Name a folder above and the first upload creates it.',
                                'Nothing here yet. Name a folder above, then upload the first document.',
                              )}
                            />
                          ) : (
                            <>
                              {/* What this page can write first: it is the half the form
                                  beside it acts on, and the repository's half is reference
                                  under it. */}
                              <Lane
                                count={filed(uploads)}
                                title={word('Filed through this site', 'What you have added')}
                                hint={word(
                                  'These are the documents this page can replace and delete.',
                                  'The documents you uploaded. You can replace or delete these.',
                                )}
                              >
                                <Browser
                                  grow={false}
                                  categories={uploads}
                                  hits={hits}
                                  folder={openIn === 'uploads' ? folder : null}
                                  onOpen={(key) => goTo(key, 'uploads')}
                                  selected={target?.source ?? null}
                                  onReplace={setTarget}
                                  onDelete={setDoomed}
                                  onOpenDocument={setReading}
                                  empty={word(
                                    'Nothing has been filed here yet. Open a folder and the form beside this puts the first document in it.',
                                    'You have not uploaded anything yet. Open a folder, then use the form beside this.',
                                  )}
                                />
                              </Lane>

                              <Lane
                                grow
                                count={filed(core)}
                                title={word('Core base documents', 'The handbook')}
                                hint={word(
                                  'Read-only here.',
                                  'You can open and read these. They are changed in the docs repository, not on this page.',
                                )}
                              >
                                <Browser
                                  readOnly
                                  categories={core}
                                  hits={hits}
                                  folder={openIn === 'core' ? folder : null}
                                  onOpen={(key) => goTo(key, 'core')}
                                  selected={null}
                                  onReplace={setTarget}
                                  onDelete={setDoomed}
                                  onOpenDocument={setReading}
                                  empty={word(
                                    'The documentation repository has nothing filed under this system — everything above was added here.',
                                    'Nothing from the handbook yet. Everything above was added here.',
                                  )}
                                />
                              </Lane>
                            </>
                          )
                        }
                      </Async>
                    </div>
                  </Section>

                  <Section
                    title={word('Add or replace a document', 'Add a document')}
                    hint={word(
                      'PDF, Word or Markdown, re-indexed as it lands — the next question is answered from it.',
                      'Upload a file. The assistant answers from it straight away.',
                    )}
                    className="border-t border-[var(--border-light)] pt-6 xl:border-l xl:border-t-0 xl:pl-8 xl:pt-0"
                  >
                    <UploadForm
                      system={system}
                      folder={folder}
                      folderLabel={folderLabel}
                      target={target}
                      onTargetChange={setTarget}
                      // Into the uploads list, whichever list the folder was opened in: this
                      // is the side that was just written to, and leaving the open row on the
                      // repository's copy of that folder would point at the wrong one.
                      onUploaded={(category) => goTo(category, 'uploads')}
                    />

                    {/* Re-indexing belongs with uploading, not with the failing questions: it
                        is the same job done by hand, for files edited on disk rather than
                        here. */}
                    {!simple && system && <ReindexForm system={system} />}
                  </Section>
                </div>
              </div>
            )
          }
        </Async>
      </PanelCard>

      {/* Dialogs, so they belong to the page rather than to a card: each draws nothing until
          a document is handed to it. */}
      <DocumentViewer
        system={system}
        document={reading}
        onClose={() => setReading(null)}
        onReplace={setTarget}
      />

      <DeleteDialog
        system={system}
        document={doomed}
        hits={doomed ? (hits.get(doomed.source) ?? 0) : 0}
        onClose={() => {
          // A document queued for replacement that has just been deleted would aim the next
          // upload at a path that is no longer there.
          if (doomed && target?.source === doomed.source) setTarget(null);
          setDoomed(null);
        }}
      />

      {/* Full width, because the questions are sentences and a half-width column cut every
          one of them in half. */}
      <PanelCard
        title="What to write next"
        hint="The failing questions, worst first. Write one document, then read “Did it help?” below in a week."
        simpleHint="The questions answered worst. Write a document for one, then read “Did it help?” below in a week."
      >
        <Async query={gaps}>
          {(data) => (
            <Table
              rows={data.items}
              pageSize={10}
              empty="No failing questions in this window."
              columns={[
                { key: 'example', label: 'Question' },
                { key: 'occurrences', label: 'Times', simple: 'Times asked', num: true },
                {
                  id: 'avg_confidence',
                  label: 'Evidence',
                  num: true,
                  advanced: true,
                  render: (g) => num(g.avg_confidence, 2),
                  sortValue: (g) => g.avg_confidence,
                },
                {
                  id: 'status',
                  label: '',
                  render: (g) => (g.status ? <Signal tone="good">{g.status}</Signal> : null),
                },
              ]}
            />
          )}
        </Async>
      </PanelCard>

      {/* The one tile row on this page, and it heads the section below it rather than the
          page. Nothing to summarise until the first document has been added, so it is
          absent rather than four zeroes. */}
      {impact.data && impact.data.items.length > 0 && (
        <TileRow tiles={impactTiles(impact.data.items, word)} />
      )}

      <PanelCard
        title="Did the documentation changes help?"
        simpleTitle="Did it help?"
        hint={
          <>
            The same measures either side of each upload, one line per change. Open a line for
            the full breakdown: <b>targeted</b> is the turns that retrieved it, <b>overall</b>{' '}
            is all traffic.
          </>
        }
        simpleHint="The fortnight before each document, against the fortnight after. Open a line to see it in full. A comparison, not a proof."
      >
        <Async query={impact}>{(data) => <ImpactList items={data.items} />}</Async>
      </PanelCard>

      {/* Usage last: reference material for the work above rather than the first thing to
          read. The browser answers "what is filed where"; these two answer "what is any of it
          doing", which is a different question and worth its own flat, sortable view. */}
      <Async query={coverage}>
        {(data) => (
          <div className="space-y-5">
            <DocumentUse data={data} />
            <EveryDocument data={data} />
          </div>
        )}
      </Async>
    </div>
  );
}
